import React from "react";
import { XIcon } from "../icons";

type SavedSpotKind = "favorite" | "fish" | "food" | "launch" | "sunset";

type SavedSpotsMapOverlayProps = {
  [key: string]: any;
};

export default function SavedSpotsMapOverlay(props: SavedSpotsMapOverlayProps) {
  const {
    savedSpotsDesktopAnchor,
    headerTopCss,
    isSavedSpotDetailMode,
    savedSpotsPanelRef,
    isDarkMode,
    pendingSavedSpotLatLng,
    selectedSavedSpot,
    savedSpots,
    savedSpotFilter,
    savedSpotFilterOptions,
    savedSpotCounts,
    isSavedSpotFilterMenuOpen,
    showSavedSpotsOnMap,
    filteredSavedSpots,
    visibleSavedSpotsList,
    savedSpotDraftKind,
    savedSpotDraftName,
    savedSpotDraftNote,
    savedSpotDraftNameLiveRef,
    savedSpotDraftNoteLiveRef,
    savedSpotNameInputRef,
    editingSavedSpotId,
    savingSavedSpotId,
    deletingSavedSpotId,
    isSelectedSavedSpotExpanded,
    isSelectedSavedSpotNoteExpanded,
    isSelectedSavedSpotMenuOpen,
    SAVED_SPOT_KIND_META,
    getSavedSpotDefaultName,
    formatSavedSpotTimestamp,
    centerMapOnSavedSpot,
    openSavedSpotDetail,
    handleShareSavedSpot,
    savePendingSavedSpot,
    setEditingSavedSpotId,
    setIsSavedSpotFilterMenuOpen,
    setIsSavedSpotsOpen,
    setIsSelectedSavedSpotExpanded,
    setIsSelectedSavedSpotMenuOpen,
    setIsSelectedSavedSpotNoteExpanded,
    setPendingSavedSpotLatLng,
    setSavedSpotDeleteConfirm,
    setSavedSpotDraftKind,
    setSavedSpotDraftName,
    setSavedSpotDraftNote,
    setSavedSpotFilter,
    setSelectedSavedSpotId,
    setShowSavedSpotsOnMap,
  } = props;

  return (
<div
  className={`absolute inset-0 overflow-hidden pointer-events-none ${savedSpotsDesktopAnchor ? "z-[999] bg-transparent" : "z-[6500] bg-black/[0.035] dark:bg-black/[0.08]"}`}
>
  <div
    className={savedSpotsDesktopAnchor ? "absolute pointer-events-none" : "absolute inset-x-0 pointer-events-auto"}
    style={savedSpotsDesktopAnchor
      ? {
          left: savedSpotsDesktopAnchor.left,
          bottom: savedSpotsDesktopAnchor.bottom,
          height: savedSpotsDesktopAnchor.height,
          width: "min(420px, calc(100% - 24px))",
        }
      : {
          // Compact/mobile keeps the proven centered drawer geometry.
          top: `calc(${headerTopCss} + 118px)`,
          bottom:
            "calc(var(--roddo-map-bottom-clearance, var(--roddo-nav-h, 84px)) + env(safe-area-inset-bottom) + 14px)",
        }}
  >
    <div
      className={savedSpotsDesktopAnchor
        ? "flex h-full w-full min-h-0 flex-col items-center justify-end"
        : `mx-auto flex h-full w-[min(90vw,420px)] min-h-0 flex-col px-3 ${isSavedSpotDetailMode ? "items-end justify-end pb-1" : "items-center justify-start"}`}
    >
      <div
        ref={savedSpotsPanelRef}
        className="pointer-events-auto roddo-panel-strong relative z-[1000] flex w-full min-h-0 flex-col overflow-hidden rounded-[22px] border"
        onClick={(e) => e.stopPropagation()}
        style={{
          background: isDarkMode
            ? "rgba(10,17,32,0.99)"
            : "linear-gradient(180deg, rgba(250,244,238,0.96), rgba(244,236,228,0.96))",
          borderColor: isDarkMode
            ? "rgba(132,151,190,0.16)"
            : "rgba(116,86,54,0.14)",
          boxShadow: isDarkMode
            ? "0 10px 28px rgba(0,0,0,0.30), inset 0 1px 0 rgba(255,255,255,0.025)"
            : "0 18px 42px rgba(89,65,37,0.16), inset 0 1px 0 rgba(255,255,255,0.62)",
          backdropFilter: "blur(10px)",
          WebkitBackdropFilter: "blur(10px)",
          maxHeight: pendingSavedSpotLatLng
            ? "min(calc(var(--roddo-visual-vh, 100dvh) - var(--roddo-header-h, 88px) - var(--roddo-map-bottom-clearance, var(--roddo-nav-h, 84px)) - 108px), 560px)"
            : isSavedSpotDetailMode
              ? "min(calc(var(--roddo-visual-vh, 100dvh) - 180px), 520px)"
              : "min(calc(var(--roddo-visual-vh, 100dvh) - var(--roddo-header-h, 88px) - var(--roddo-map-bottom-clearance, var(--roddo-nav-h, 84px)) - 108px), 620px)",
        }}
      >
        <div className="relative z-[2] flex shrink-0 justify-center pt-2.5">
          <div
            className="h-1 w-10 rounded-full sm:w-12"
            style={{
              background: isDarkMode
                ? "rgba(255,255,255,0.20)"
                : "rgba(80,62,45,0.16)",
            }}
          />
        </div>

        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background: isDarkMode
              ? "none"
              : "radial-gradient(120% 100% at 20% 0%, rgba(255,255,255,0.52), transparent 58%)",
          }}
        />

        <div className="relative flex min-h-0 flex-1 flex-col overflow-y-auto overscroll-contain px-4 pb-4 pt-2.5">
          <div className="flex items-start gap-3 shrink-0">
            <div
              className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl text-[24px]"
              style={{
                background: isDarkMode
                  ? "linear-gradient(180deg, rgba(255,210,122,0.22), rgba(255,177,79,0.14))"
                  : "linear-gradient(180deg, rgba(255,235,186,0.88), rgba(255,218,145,0.72))",
                border: isDarkMode
                  ? "1px solid rgba(255,255,255,0.10)"
                  : "1px solid rgba(255,255,255,0.72)",
              }}
            >
              ★
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="flex w-full justify-center">
                    <div
                      className="rounded-full px-4 py-1.5 text-center text-[18px] font-extrabold tracking-[0.04em]"
                      style={{
                        color: isDarkMode ? "#FFFFFF" : "#22313D",
                        background: isDarkMode
                          ? "transparent"
                          : "linear-gradient(180deg, rgba(255,255,255,0.82), rgba(248,241,233,0.72))",
                        border: isDarkMode
                          ? "1px solid rgba(255,255,255,0.10)"
                          : "1px solid rgba(91,67,49,0.10)",
                        boxShadow: isDarkMode
                          ? "0 8px 18px rgba(0,0,0,0.18), inset 0 1px 0 rgba(255,255,255,0.05)"
                          : "0 8px 16px rgba(89,65,37,0.08), inset 0 1px 0 rgba(255,255,255,0.72)",
                        textShadow: isDarkMode
                          ? "0 2px 8px rgba(0,0,0,0.34)"
                          : "0 1px 0 rgba(255,255,255,0.62)",
                      }}
                    >
                      {isSavedSpotDetailMode
                        ? "Saved spot"
                        : "Saved spots"}
                    </div>
                  </div>
                </div>

                <button
                  type="button"
                  onClick={() => {
                    setIsSavedSpotsOpen(false);
                    setPendingSavedSpotLatLng(null);
                    setSelectedSavedSpotId(null);
                  }}
                  className="grid h-9 w-9 shrink-0 place-items-center rounded-full transition-[transform,background-color] active:scale-95"
                  style={{
                    background: isDarkMode
                      ? "rgba(255,255,255,0.08)"
                      : "rgba(255,255,255,0.52)",
                    color: isDarkMode ? "#F8FAFF" : "#2b2b2b",
                    border: isDarkMode
                      ? "1px solid rgba(255,255,255,0.10)"
                      : "1px solid rgba(60,40,20,0.10)",
                  }}
                  aria-label="Close saved spots"
                >
                  <XIcon className="h-4 w-4" />
                </button>
              </div>
            </div>
          </div>

          <div
            className={`relative mt-3 shrink-0 ${isSavedSpotDetailMode ? "hidden" : ""}`}
          >
            {(() => {
              const activeFilterOption =
                savedSpotFilterOptions.find(
                  (item) => item.key === savedSpotFilter,
                ) || savedSpotFilterOptions[0];
              const activeCount =
                savedSpotCounts[activeFilterOption.key];
              return (
                <>
                  <div className="flex items-stretch gap-2">
                    <button
                      type="button"
                      onClick={() =>
                        setIsSavedSpotFilterMenuOpen((prev) => !prev)
                      }
                      className="flex min-w-0 flex-1 items-center justify-between rounded-[18px] border px-3 py-2.5 text-left transition-transform active:scale-[0.99]"
                      style={{
                        background: isDarkMode
                          ? "rgba(16,24,39,0.98)"
                          : "linear-gradient(180deg, rgba(255,255,255,0.88), rgba(245,238,231,0.96))",
                        borderColor: isSavedSpotFilterMenuOpen
                          ? isDarkMode
                            ? "rgba(138,112,242,0.28)"
                            : "rgba(124,199,255,0.30)"
                          : isDarkMode
                            ? "rgba(132,151,190,0.14)"
                            : "rgba(91,67,49,0.10)",
                        boxShadow: isDarkMode
                          ? "0 10px 18px rgba(0,0,0,0.16), inset 0 1px 0 rgba(255,255,255,0.04)"
                          : "0 8px 16px rgba(89,65,37,0.08), inset 0 1px 0 rgba(255,255,255,0.72)",
                        color: isDarkMode ? "#FFFFFF" : "#22313D",
                      }}
                      aria-label="Choose saved spot filter"
                    >
                      <div className="min-w-0">
                        <div
                          className="text-[10px] font-black uppercase tracking-[0.14em]"
                          style={{
                            color: isDarkMode
                              ? "rgba(239,244,255,0.88)"
                              : "#5B5147",
                          }}
                        >
                          Filter
                        </div>
                        <div
                          className="mt-1 flex items-center gap-2 text-[13px] font-black uppercase tracking-[0.08em]"
                          style={{
                            color: isDarkMode ? "#BDEFF5" : "#075985",
                          }}
                        >
                          <span>{activeFilterOption.emoji}</span>
                          <span>{activeFilterOption.label}</span>
                          <span className="opacity-90">
                            ({activeCount})
                          </span>
                        </div>
                      </div>
                      <div
                        className="text-[16px] font-black transition-transform"
                        style={{
                          color: isDarkMode ? "#FFFFFF" : "#22313D",
                          transform: isSavedSpotFilterMenuOpen
                            ? "rotate(180deg)"
                            : "rotate(0deg)",
                        }}
                      >
                        ▾
                      </div>
                    </button>

                    <button
                      type="button"
                      onClick={() =>
                        setShowSavedSpotsOnMap((prev) => !prev)
                      }
                      className="grid h-[52px] w-[52px] shrink-0 place-items-center rounded-[18px] border transition-transform active:scale-[0.96]"
                      style={{
                        background: showSavedSpotsOnMap
                          ? isDarkMode
                            ? "rgba(16,24,39,0.98)"
                            : "linear-gradient(180deg, rgba(255,255,255,0.88), rgba(245,238,231,0.96))"
                          : isDarkMode
                            ? "rgba(12,20,35,0.98)"
                            : "linear-gradient(180deg, rgba(250,246,241,0.94), rgba(239,231,222,0.98))",
                        borderColor: showSavedSpotsOnMap
                          ? isDarkMode
                            ? "rgba(138,112,242,0.22)"
                            : "rgba(124,199,255,0.24)"
                          : isDarkMode
                            ? "rgba(132,151,190,0.14)"
                            : "rgba(91,67,49,0.10)",
                        boxShadow: isDarkMode
                          ? "0 10px 18px rgba(0,0,0,0.16), inset 0 1px 0 rgba(255,255,255,0.04)"
                          : "0 8px 16px rgba(89,65,37,0.08), inset 0 1px 0 rgba(255,255,255,0.72)",
                      }}
                      aria-label={
                        showSavedSpotsOnMap
                          ? "Hide saved spots on map"
                          : "Show saved spots on map"
                      }
                      title={
                        showSavedSpotsOnMap
                          ? "Hide saved spots on map"
                          : "Show saved spots on map"
                      }
                    >
                      <span
                        className="text-[24px] leading-none"
                        style={{
                          filter: showSavedSpotsOnMap
                            ? "none"
                            : "grayscale(0.12)",
                          opacity: showSavedSpotsOnMap ? 1 : 0.82,
                        }}
                      >
                        {showSavedSpotsOnMap ? "🐵" : "🙈"}
                      </span>
                    </button>
                  </div>

                  {isSavedSpotFilterMenuOpen ? (
                    <div
                      className="mt-2 grid gap-2 rounded-[18px] border p-2"
                      style={{
                        background: isDarkMode
                          ? "rgba(12,20,35,0.985)"
                          : "linear-gradient(180deg, rgba(255,255,255,0.96), rgba(244,236,250,0.98))",
                        borderColor: isDarkMode
                          ? "rgba(132,151,190,0.14)"
                          : "rgba(91,67,49,0.10)",
                      }}
                    >
                      {savedSpotFilterOptions.map((item) => {
                        const active = savedSpotFilter === item.key;
                        const count = savedSpotCounts[item.key];
                        return (
                          <button
                            key={item.key}
                            type="button"
                            onClick={() => {
                              setSavedSpotFilter(item.key);
                              setIsSavedSpotFilterMenuOpen(false);
                            }}
                            className="flex items-center justify-between rounded-[14px] px-3 py-2 text-left transition-transform active:scale-[0.98]"
                            style={{
                              background: active
                                ? isDarkMode
                                  ? "rgba(138,112,242,0.12)"
                                  : "rgba(223,241,255,0.94)"
                                : isDarkMode
                                  ? "rgba(255,255,255,0.06)"
                                  : "rgba(255,255,255,0.66)",
                              border: active
                                ? isDarkMode
                                  ? "1px solid rgba(146,120,236,0.36)"
                                  : "1px solid rgba(124,199,255,0.36)"
                                : isDarkMode
                                  ? "1px solid rgba(255,255,255,0.10)"
                                  : "1px solid rgba(91,67,49,0.08)",
                              color: active
                                ? isDarkMode
                                  ? "#BDEFF5"
                                  : "#0d5b8a"
                                : isDarkMode
                                  ? "#F8FAFF"
                                  : "#2f3d45",
                            }}
                          >
                            <div className="flex items-center gap-2">
                              <span className="text-[15px] leading-none">
                                {item.emoji}
                              </span>
                              <span className="text-[12px] font-black uppercase tracking-[0.08em]">
                                {item.label}
                              </span>
                            </div>
                            <span className="text-[12px] font-black opacity-90">
                              ({count})
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  ) : null}
                </>
              );
            })()}
          </div>

          {pendingSavedSpotLatLng ? (
            <div
              className="mt-3 rounded-[20px] border p-3"
              style={{
                background: isDarkMode
                  ? "rgba(12,20,35,0.985)"
                  : "linear-gradient(180deg, rgba(255,255,255,0.96), rgba(244,236,250,0.98))",
                borderColor: isDarkMode
                  ? "rgba(132,151,190,0.14)"
                  : "rgba(91,67,49,0.10)",
              }}
            >
              <div
                className="text-[11px] font-black uppercase tracking-[0.16em]"
                style={{
                  color: isDarkMode
                    ? "rgba(239,244,255,0.90)"
                    : "#564C42",
                }}
              >
                Selected spot
              </div>
              <div className="mt-2">
                <label
                  className="mb-1 block text-[10px] font-black uppercase tracking-[0.14em]"
                  style={{
                    color: isDarkMode
                      ? "rgba(235,241,255,0.86)"
                      : "#655B50",
                  }}
                >
                  Spot type
                </label>

                <div className="relative">
                  <select
                    value={savedSpotDraftKind}
                    onChange={(e) => {
                      const nextKind = e.target
                        .value as SavedSpotKind;
                      const currentMeta =
                        SAVED_SPOT_KIND_META[savedSpotDraftKind];
                      const currentDefaultName =
                        getSavedSpotDefaultName(savedSpotDraftKind);
                      const trimmedName = savedSpotDraftNameLiveRef.current.trim();

                      setSavedSpotDraftKind(nextKind);

                      if (
                        trimmedName &&
                        (trimmedName === currentMeta.label ||
                          trimmedName === currentDefaultName)
                      ) {
                        const nextName = getSavedSpotDefaultName(nextKind);
                        savedSpotDraftNameLiveRef.current = nextName;
                        if (savedSpotNameInputRef.current) {
                          savedSpotNameInputRef.current.value = nextName;
                        }
                      }
                    }}
                    className="w-full appearance-none rounded-2xl border px-3 py-2.5 pr-10 text-sm font-black focus:outline-none"
                    style={{
                      background: isDarkMode
                        ? "rgba(16,24,42,0.96)"
                        : "rgba(255,255,255,0.92)",
                      color: isDarkMode ? "#FFFFFF" : "#22313D",
                      borderColor: isDarkMode
                        ? "rgba(132,151,190,0.16)"
                        : "rgba(91,67,49,0.12)",
                      boxShadow: isDarkMode
                        ? "inset 0 1px 0 rgba(255,255,255,0.08)"
                        : "inset 0 1px 0 rgba(255,255,255,0.72)",
                    }}
                  >
                    {(
                      [
                        "favorite",
                        "fish",
                        "food",
                        "launch",
                        "sunset",
                      ] as SavedSpotKind[]
                    ).map((kind) => {
                      const meta = SAVED_SPOT_KIND_META[kind];
                      return (
                        <option key={kind} value={kind}>
                          {meta.emoji} {meta.label}
                        </option>
                      );
                    })}
                  </select>

                  <div
                    className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[12px] font-black"
                    style={{
                      color: isDarkMode
                        ? "rgba(248,250,255,0.70)"
                        : "rgba(47,61,69,0.62)",
                    }}
                  >
                    ▾
                  </div>
                </div>
              </div>

              <input
                key={`saved-spot-name-${editingSavedSpotId || "new"}-${pendingSavedSpotLatLng?.lat ?? ""}-${pendingSavedSpotLatLng?.lng ?? ""}`}
                type="text"
                defaultValue={savedSpotDraftName}
                onChange={(e) => {
                  savedSpotDraftNameLiveRef.current = e.target.value;
                }}
                placeholder="Name this spot..."
                ref={savedSpotNameInputRef}
                className="mt-2 w-full rounded-2xl border px-3 py-2 text-sm focus:outline-none"
                style={{
                  background: isDarkMode
                    ? "rgba(18,24,45,0.62)"
                    : "rgba(255,255,255,0.88)",
                  color: isDarkMode ? "#FFFFFF" : "#22313D",
                  borderColor: isDarkMode
                    ? "rgba(132,151,190,0.14)"
                    : "rgba(91,67,49,0.10)",
                }}
              />
              <textarea
                key={`saved-spot-note-${editingSavedSpotId || "new"}-${pendingSavedSpotLatLng?.lat ?? ""}-${pendingSavedSpotLatLng?.lng ?? ""}`}
                defaultValue={savedSpotDraftNote}
                onChange={(e) => {
                  savedSpotDraftNoteLiveRef.current = e.target.value;
                }}
                placeholder="Optional note"
                rows={3}
                className="mt-2 w-full resize-none rounded-2xl border px-3 py-2 text-sm focus:outline-none"
                style={{
                  background: isDarkMode
                    ? "rgba(18,24,45,0.62)"
                    : "rgba(255,255,255,0.88)",
                  color: isDarkMode ? "#FFFFFF" : "#22313D",
                  borderColor: isDarkMode
                    ? "rgba(132,151,190,0.14)"
                    : "rgba(91,67,49,0.10)",
                }}
              />
              <div className="mt-3 grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={savePendingSavedSpot}
                  disabled={!!savingSavedSpotId}
                  className="h-10 rounded-2xl px-3 text-[13px] font-black text-[#fff7ea] transition-transform active:scale-[0.98] disabled:cursor-wait disabled:opacity-70"
                  style={{
                    background:
                      "linear-gradient(180deg, rgba(241,100,46,0.96), rgba(209,80,32,0.96))",
                    border: "1px solid rgba(255,255,255,0.28)",
                    boxShadow: "0 10px 20px rgba(209,80,32,0.20)",
                  }}
                >
                  {savingSavedSpotId ? (editingSavedSpotId ? "Updating..." : "Saving...") : (editingSavedSpotId ? "Update spot" : "Save spot")}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setPendingSavedSpotLatLng(null);
                    savedSpotDraftNameLiveRef.current = "";
                    savedSpotDraftNoteLiveRef.current = "";
                    setSavedSpotDraftName("");
                    setSavedSpotDraftNote("");
                    setSavedSpotDraftKind("favorite");
                    setEditingSavedSpotId(null);
                    setIsSavedSpotsOpen(false);
                  }}
                  className="h-10 rounded-2xl px-3 text-[13px] font-black transition-transform active:scale-[0.98]"
                  style={{
                    background: isDarkMode
                      ? "rgba(255,255,255,0.08)"
                      : "rgba(255,255,255,0.72)",
                    border: isDarkMode
                      ? "1px solid rgba(255,255,255,0.12)"
                      : "1px solid rgba(91,67,49,0.10)",
                    color: isDarkMode ? "#FFFFFF" : "#22313D",
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : null}

          {selectedSavedSpot
            ? (() => {
                const selectedMeta =
                  SAVED_SPOT_KIND_META[selectedSavedSpot.kind];
                const selectedNote = String(
                  selectedSavedSpot.note || "",
                ).trim();
                const noteNeedsClamp = selectedNote.length > 140;
                const visibleNote =
                  noteNeedsClamp && !isSelectedSavedSpotNoteExpanded
                    ? `${selectedNote.slice(0, 137).trimEnd()}...`
                    : selectedNote;

                return (
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      setIsSelectedSavedSpotMenuOpen(false);
                      setIsSelectedSavedSpotExpanded((prev) => !prev);
                    }}
                    onKeyDown={(event) => {
                      if (
                        event.key === "Enter" ||
                        event.key === " "
                      ) {
                        event.preventDefault();
                        setIsSelectedSavedSpotMenuOpen(false);
                        setIsSelectedSavedSpotExpanded(
                          (prev) => !prev,
                        );
                      }
                    }}
                    className="relative mt-3 cursor-pointer rounded-[20px] border p-4 transition-transform active:scale-[0.99]"
                    aria-expanded={isSelectedSavedSpotExpanded}
                    style={{
                      background: isDarkMode
                        ? "rgba(12,20,35,0.985)"
                        : "linear-gradient(180deg, rgba(255,255,255,0.96), rgba(244,236,250,0.98))",
                      borderColor: isDarkMode
                        ? "rgba(132,151,190,0.14)"
                        : "rgba(91,67,49,0.10)",
                      boxShadow: isDarkMode
                        ? "0 14px 28px rgba(0,0,0,0.22), inset 0 1px 0 rgba(255,255,255,0.04)"
                        : "0 12px 22px rgba(89,65,37,0.10), inset 0 1px 0 rgba(255,255,255,0.72)",
                    }}
                  >
                    <div className="flex items-start gap-3 pr-16">
                      <div
                        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl text-[22px]"
                        style={{
                          background: isDarkMode
                            ? "transparent"
                            : "linear-gradient(180deg, rgba(255,255,255,0.96), rgba(248,241,233,0.92))",
                          border: isDarkMode
                            ? "1px solid rgba(255,255,255,0.12)"
                            : "1px solid rgba(91,67,49,0.10)",
                        }}
                      >
                        {selectedMeta.emoji}
                      </div>

                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                          <div
                            className="truncate text-[17px] font-black"
                            style={{
                              color: isDarkMode
                                ? "#F8FAFF"
                                : "#2f3d45",
                            }}
                          >
                            {selectedSavedSpot.name}
                          </div>
                          <div
                            className="rounded-full px-2 py-0.5 text-[10px] font-black uppercase tracking-[0.14em]"
                            style={{
                              background: isDarkMode
                                ? "rgba(255,255,255,0.08)"
                                : "rgba(255,255,255,0.72)",
                              color: isDarkMode
                                ? selectedMeta.accentDark
                                : selectedMeta.accent,
                              border: isDarkMode
                                ? "1px solid rgba(255,255,255,0.10)"
                                : "1px solid rgba(91,67,49,0.10)",
                            }}
                          >
                            {selectedMeta.label}
                          </div>
                        </div>

                        <div
                          className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] font-semibold"
                          style={{
                            color: isDarkMode
                              ? "rgba(235,241,255,0.88)"
                              : "#554C43",
                          }}
                        >
                          <span>
                            {formatSavedSpotTimestamp(
                              selectedSavedSpot.createdAt,
                            )}
                          </span>
                          <span>
                            {selectedSavedSpot.lat.toFixed(4)},{" "}
                            {selectedSavedSpot.lng.toFixed(4)}
                          </span>
                        </div>

                        <div
                          className="mt-2 text-[10px] font-black uppercase tracking-[0.14em]"
                          style={{
                            color: isDarkMode
                              ? "rgba(226,234,255,0.76)"
                              : "#6F6458",
                          }}
                        >
                          {isSavedSpotDetailMode
                            ? "Saved location detail"
                            : isSelectedSavedSpotExpanded
                              ? "Tap card to collapse"
                              : "Tap card to expand"}
                        </div>
                      </div>
                    </div>

                    <div className="absolute right-4 top-4 z-[1]">
                      <button
                        type="button"
                        onClick={(event) => {
                          event.stopPropagation();
                          setIsSelectedSavedSpotMenuOpen(
                            (prev) => !prev,
                          );
                        }}
                        className="grid h-9 w-9 place-items-center rounded-full text-[18px] font-black leading-none transition-transform active:scale-95"
                        style={{
                          background: isDarkMode
                            ? "rgba(255,255,255,0.08)"
                            : "rgba(255,255,255,0.72)",
                          border: isDarkMode
                            ? "1px solid rgba(255,255,255,0.12)"
                            : "1px solid rgba(91,67,49,0.10)",
                          color: isDarkMode ? "#FFFFFF" : "#22313D",
                        }}
                        aria-label="Saved spot actions"
                        aria-expanded={isSelectedSavedSpotMenuOpen}
                      >
                        ...
                      </button>

                      {isSelectedSavedSpotMenuOpen ? (
                        <div
                          className="absolute right-0 mt-2 w-44 overflow-hidden rounded-[18px] border"
                          style={{
                            background: isDarkMode
                              ? "linear-gradient(180deg, rgba(34,28,70,0.98), rgba(28,22,58,0.98))"
                              : "linear-gradient(180deg, rgba(255,255,255,0.98), rgba(247,241,235,0.98))",
                            borderColor: isDarkMode
                              ? "rgba(132,151,190,0.14)"
                              : "rgba(91,67,49,0.10)",
                            boxShadow: isDarkMode
                              ? "0 18px 36px rgba(0,0,0,0.34)"
                              : "0 16px 28px rgba(89,65,37,0.16)",
                          }}
                        >
                          <button
                            type="button"
                            onClick={() => {
                              setIsSelectedSavedSpotMenuOpen(false);
                              centerMapOnSavedSpot(
                                selectedSavedSpot,
                                15,
                                { revealAbovePanel: true },
                              );
                            }}
                            className="flex w-full items-center justify-between px-4 py-3 text-left text-[13px] font-black transition-colors"
                            style={{
                              color: isDarkMode
                                ? "#F8FAFF"
                                : "#2f3d45",
                            }}
                          >
                            <span>Go to location</span>
                            <span className="text-[15px]">⌖</span>
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              setIsSelectedSavedSpotMenuOpen(false);
                              void handleShareSavedSpot(
                                selectedSavedSpot,
                              );
                            }}
                            className="flex w-full items-center justify-between border-t px-4 py-3 text-left text-[13px] font-black transition-colors"
                            style={{
                              color: isDarkMode
                                ? "#F8FAFF"
                                : "#2f3d45",
                              borderColor: isDarkMode
                                ? "rgba(255,255,255,0.08)"
                                : "rgba(91,67,49,0.08)",
                            }}
                          >
                            <span>Share</span>
                            <span className="text-[15px]">↗</span>
                          </button>
                          <button
                            type="button"
                            disabled={
                              deletingSavedSpotId ===
                              selectedSavedSpot.id
                            }
                            onClick={() => {
                              setIsSelectedSavedSpotMenuOpen(false);
                              setSavedSpotDeleteConfirm(selectedSavedSpot);
                            }}
                            className="flex w-full items-center justify-between border-t px-4 py-3 text-left text-[13px] font-black transition-colors disabled:cursor-wait disabled:opacity-70"
                            style={{
                              color: "#ff8d78",
                              borderColor: isDarkMode
                                ? "rgba(255,255,255,0.08)"
                                : "rgba(91,67,49,0.08)",
                            }}
                          >
                            <span>
                              {deletingSavedSpotId ===
                              selectedSavedSpot.id
                                ? "Deleting..."
                                : "Delete"}
                            </span>
                            <span className="text-[15px]">×</span>
                          </button>
                        </div>
                      ) : null}
                    </div>

                    {isSelectedSavedSpotExpanded ? (
                      <div
                        className="mt-4 rounded-[18px] border px-3 py-3"
                        style={{
                          background: isDarkMode
                            ? "rgba(255,255,255,0.05)"
                            : "rgba(255,255,255,0.62)",
                          borderColor: isDarkMode
                            ? "rgba(255,255,255,0.08)"
                            : "rgba(91,67,49,0.08)",
                        }}
                      >
                        <div
                          className="inline-flex items-center rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.14em]"
                          style={{
                            background: isDarkMode
                              ? "rgba(255,255,255,0.075)"
                              : "rgba(255,255,255,0.82)",
                            borderColor: isDarkMode
                              ? "rgba(255,255,255,0.11)"
                              : "rgba(91,67,49,0.10)",
                            color: isDarkMode
                              ? "#F0F4FF"
                              : "#51483F",
                            boxShadow: isDarkMode
                              ? "inset 0 1px 0 rgba(255,255,255,0.05)"
                              : "0 4px 10px rgba(89,65,37,0.05), inset 0 1px 0 rgba(255,255,255,0.88)",
                          }}
                        >
                          Details
                        </div>
                        {selectedNote ? (
                          <>
                            <p
                              className="mt-2 text-[13px] leading-5"
                              style={{
                                color: isDarkMode
                                  ? "rgba(232,236,255,0.88)"
                                  : "#5F5A52",
                              }}
                            >
                              {visibleNote}
                            </p>
                            {noteNeedsClamp ? (
                              <button
                                type="button"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  setIsSelectedSavedSpotNoteExpanded(
                                    (prev) => !prev,
                                  );
                                }}
                                className="mt-2 text-[11px] font-black uppercase tracking-[0.12em]"
                                style={{
                                  color: isDarkMode
                                    ? "#BDEFF5"
                                    : "#0d5b8a",
                                }}
                              >
                                {isSelectedSavedSpotNoteExpanded
                                  ? "Show less"
                                  : "Show more"}
                              </button>
                            ) : null}
                          </>
                        ) : (
                          <p
                            className="mt-2 text-[13px] leading-5"
                            style={{
                              color: isDarkMode
                                ? "rgba(232,236,255,0.62)"
                                : "#7a7268",
                            }}
                          >
                            No note yet. This spot is still ready as a
                            quick return point on the map.
                          </p>
                        )}
                      </div>
                    ) : null}
                  </div>
                );
              })()
            : null}

          {!pendingSavedSpotLatLng && !isSavedSpotDetailMode ? (
            <div className="mt-2.5 min-h-0 flex-1 overflow-y-auto overscroll-contain pr-0.5 sm:mt-3 sm:pr-1">
              <div className="grid gap-2.5 pb-1">
                {visibleSavedSpotsList.length ? (
                  visibleSavedSpotsList.map((spot) => {
                    const meta = SAVED_SPOT_KIND_META[spot.kind];
                    return (
                      <button
                        key={spot.id}
                        type="button"
                        onClick={() => {
                          openSavedSpotDetail(spot, 15);
                        }}
                        className="roddo-pressable w-full border px-3 py-3 text-left transition-all active:scale-[0.98]"
                        style={{
                          background: isDarkMode
                            ? "rgba(16,24,39,0.98)"
                            : "linear-gradient(180deg, rgba(255,255,255,0.88), rgba(245,238,231,0.96))",
                          borderRadius: 18,
                          borderColor: isDarkMode
                            ? "rgba(132,151,190,0.14)"
                            : "rgba(91,67,49,0.10)",
                          boxShadow: isDarkMode
                            ? "0 10px 18px rgba(0,0,0,0.16), inset 0 1px 0 rgba(255,255,255,0.04)"
                            : "0 8px 16px rgba(89,65,37,0.08), inset 0 1px 0 rgba(255,255,255,0.72)",
                          color: isDarkMode ? "#FFFFFF" : "#22313D",
                        }}
                      >
                        <div className="flex items-center justify-between gap-3">
                          <div className="min-w-0">
                            <div
                              className="truncate text-[15px] font-black"
                              style={{
                                color: isDarkMode
                                  ? "#F5F8FF"
                                  : "#2D3957",
                              }}
                            >
                              <span className="mr-2">
                                {meta.emoji}
                              </span>
                              {spot.name}
                            </div>
                            <div
                              className="mt-1 truncate text-[11px] font-black uppercase tracking-[0.12em]"
                              style={{
                                color: isDarkMode
                                  ? meta.accentDark
                                  : meta.accent,
                              }}
                            >
                              {meta.label}
                            </div>
                          </div>
                          <div
                            className="text-[11px] font-black uppercase tracking-[0.08em]"
                            style={{
                              color: isDarkMode
                                ? "#BDEFF5"
                                : "#0d5b8a",
                            }}
                          >
                            View
                          </div>
                        </div>
                      </button>
                    );
                  })
                ) : (
                  <div
                    className="rounded-[20px] border border-dashed p-5 text-center"
                    style={{
                      borderColor: isDarkMode
                        ? "rgba(132,151,190,0.14)"
                        : "rgba(91,67,49,0.16)",
                    }}
                  >
                    <div
                      className="text-[14px] font-black"
                      style={{
                        color: isDarkMode ? "#FFFFFF" : "#22313D",
                      }}
                    >
                      {filteredSavedSpots.length
                        ? "No other saved spots in this filter"
                        : savedSpots.length
                          ? "No matches for this filter"
                          : "No saved spots yet"}
                    </div>
                    <p
                      className="mt-2 text-[13px] leading-5"
                      style={{
                        color: isDarkMode
                          ? "rgba(240,244,255,0.86)"
                          : "#554C43",
                      }}
                    >
                      {filteredSavedSpots.length
                        ? "The selected spot is already expanded above. Pick another filter or add a new saved spot."
                        : savedSpots.length
                          ? "Try another filter or double-click the map to add a new saved spot."
                          : "Double-click anywhere on the map to drop a favorite. Long-press still opens the full marker picker for favorite, launch, or sunset spots."}
                    </p>
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  </div>
</div>
  );
}
